import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ThemeColors } from './companyTheme';

interface CompanyThemePreviewProps {
  colors: ThemeColors;
  companyName: string;
  /** Object URL of the logo, or null for the company-name placeholder. */
  logoSrc: string | null;
}

const STAGES = [
  { number: 1, title: 'stage1', amount: 'stage1Amount', date: 'stage1Date' },
  { number: 2, title: 'stage2', amount: 'stage2Amount', date: 'stage2Date' },
] as const;

/**
 * Miniature contract in the chosen colours, laid out like the contract
 * document: logo or name placeholder, the contract label and dividers in the
 * accent colour, title, parties, assignment, price with ROT, two stages and an
 * attachment. The colours come from data, so they are passed as CSS variables
 * (docs/styling.md §6).
 */
export function CompanyThemePreview({ colors, companyName, logoSrc }: CompanyThemePreviewProps) {
  const { t } = useTranslation();
  const p = (key: string, options?: Record<string, unknown>) => t(`settings.theme.preview.${key}`, options);
  const style = {
    '--company-theme-heading': colors.headingColor,
    '--company-theme-text': colors.textColor,
    '--company-theme-accent': colors.accentColor,
  } as React.CSSProperties;

  return (
    <article
      className="company-theme-preview"
      style={style}
      aria-label={p('aria')}
      data-testid="company-theme-preview"
    >
      <header className="company-theme-preview-header">
        {logoSrc ? (
          <img src={logoSrc} alt={t('settings.theme.logo.alt', { company: companyName })} className="company-theme-logo-img" />
        ) : (
          <span className="company-theme-preview-company">{companyName || '—'}</span>
        )}
        <span className="company-theme-preview-chip">{p('chip')}</span>
      </header>
      <hr className="company-theme-preview-divider" />

      <p className="company-theme-preview-label">{p('contractLabel')}</p>
      <h4 className="company-theme-preview-title">{p('heading')}</h4>
      <p className="company-theme-preview-muted mb-0">{p('subtitle')}</p>

      <section className="company-theme-preview-section">
        <h5 className="company-theme-preview-heading">{p('partiesTitle')}</h5>
        <div className="company-theme-preview-parties">
          <div className="company-theme-preview-party">
            <span className="company-theme-preview-role">{p('companyRole')}</span>
            <span className="company-theme-preview-party-name">{companyName || '—'}</span>
          </div>
          <div className="company-theme-preview-party">
            <span className="company-theme-preview-role">{p('customerRole')}</span>
            <span className="company-theme-preview-party-name">{p('customerName')}</span>
          </div>
        </div>
      </section>

      <section className="company-theme-preview-section">
        <h5 className="company-theme-preview-heading">{p('assignmentTitle')}</h5>
        <p className="company-theme-preview-body">{p('body')}</p>
      </section>

      <section className="company-theme-preview-section">
        <h5 className="company-theme-preview-heading">{p('priceTitle')}</h5>
        <dl className="company-theme-preview-amounts">
          <div>
            <dt>{p('gross')}</dt>
            <dd>{p('grossAmount')}</dd>
          </div>
          <div>
            <dt>{p('rot')}</dt>
            <dd>{p('rotAmount')}</dd>
          </div>
          <div className="is-total">
            <dt>{p('total')}</dt>
            <dd>{p('totalAmount')}</dd>
          </div>
        </dl>
      </section>

      <section className="company-theme-preview-section">
        <h5 className="company-theme-preview-heading">{p('stagesTitle')}</h5>
        <ol className="company-theme-preview-stages">
          {STAGES.map(stage => (
            <li key={stage.number} className="company-theme-preview-stage">
              <span className="company-theme-preview-stage-number" aria-hidden="true">{stage.number}</span>
              <span className="company-theme-preview-stage-body">
                <span className="company-theme-preview-role">{p('stageLabel', { number: stage.number })}</span>
                <span className="company-theme-preview-party-name">{p(stage.title)}</span>
                <span className="company-theme-preview-muted">{p(stage.date)}</span>
              </span>
              <span className="company-theme-preview-stage-amount">{p(stage.amount)}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="company-theme-preview-section">
        <h5 className="company-theme-preview-heading">{p('attachmentsTitle')}</h5>
        <p className="company-theme-preview-body d-flex align-items-center gap-2 mb-0">
          <i className="feather-file-text company-theme-preview-icon" aria-hidden="true"></i>
          {p('attachment')}
        </p>
      </section>
    </article>
  );
}

export default CompanyThemePreview;
